/**
 * bm-modules.js — Cargador de módulos bajo demanda.
 *
 * El script se incluye como un módulo parser-inserted (defer por defecto), por
 * lo que su primer escaneo ve el documento ya parseado. Los imports dinámicos
 * empiezan entonces, pero su promesa NO forma parte del contrato de
 * DOMContentLoaded: los consumidores que necesiten esperar la hidratación
 * inicial deben usar window.BMModules.ready o el evento bm:modules-ready.
 *
 * Después, un MutationObserver cubre HTML añadido por section rendering, quick
 * add, cart drawer, filtros o el editor. Esas cargas posteriores se agrupan una
 * vez por frame y no bloquean el documento.
 */

const LOG_PREFIX = '[BMModules]';

/** @type {MutationObserver | null} */
let observer = null;

/** @type {Map<string, string>} url del asset -> selector que lo activa */
const pending = new Map();

/**
 * Importa un módulo y registra el fallo sin convertirlo en una excepción global.
 * La promesa se mantiene rechazada para que Promise.allSettled refleje el estado.
 *
 * @param {string} url
 * @returns {Promise<unknown>}
 */
function importModule(url) {
  return import(url).catch((error) => {
    console.error(`${LOG_PREFIX} No se pudo importar ${url}`, error);
    throw error;
  });
}

/**
 * Retira de pending los módulos cuyo selector ya está en el documento.
 * Los selectores inválidos también se retiran de verdad y se notifican una vez.
 *
 * @returns {string[]}
 */
function takeMatches() {
  /** @type {string[]} */
  const matches = [];

  for (const [url, selector] of pending) {
    let hit = false;

    try {
      hit = document.querySelector(selector) !== null;
    } catch (error) {
      pending.delete(url);
      console.error(`${LOG_PREFIX} Selector inválido para ${url}: ${selector}`, error);
      continue;
    }

    if (hit) {
      pending.delete(url);
      matches.push(url);
    }
  }

  return matches;
}

/**
 * Importa en paralelo una lista ya seleccionada de módulos.
 *
 * @param {string[]} urls
 * @returns {Promise<PromiseSettledResult<unknown>[]>}
 */
function importAll(urls) {
  return Promise.allSettled(urls.map(importModule));
}

/**
 * Carga todos los módulos visibles en el DOM inicial. Se repite hasta quedar
 * estable para cubrir elementos que un custom element pueda añadir al conectar.
 */
async function hydrateInitialDocument() {
  /** @type {PromiseSettledResult<unknown>[]} */
  const results = [];

  while (pending.size) {
    const matches = takeMatches();
    if (!matches.length) return results;
    results.push(...(await importAll(matches)));
  }

  return results;
}

/**
 * Programa un único escaneo global por frame. Escanear document una vez evita
 * multiplicar el trabajo por cada nodo añadido durante un morph grande.
 */
function observeFutureElements() {
  if (!pending.size || typeof MutationObserver !== 'function') return;

  let queued = false;

  const flush = () => {
    queued = false;

    const matches = takeMatches();
    if (matches.length) {
      // Las mutaciones posteriores son mejoras incrementales: no bloquean nada.
      void importAll(matches);
    }

    if (!pending.size && observer) {
      observer.disconnect();
      observer = null;
    }
  };

  observer = new MutationObserver((records) => {
    const hasAddedElements = records.some((record) =>
      Array.from(record.addedNodes).some((node) => node.nodeType === Node.ELEMENT_NODE)
    );

    if (!hasAddedElements || queued) return;
    queued = true;

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(flush);
    } else {
      window.setTimeout(flush, 0);
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
}

async function initialize() {
  const source = document.getElementById('bm-module-map');
  if (!source) {
    console.error(`${LOG_PREFIX} No se encontró #bm-module-map`);
    return [];
  }

  /** @type {unknown} */
  let parsed;

  try {
    parsed = JSON.parse(source.textContent || '{}');
  } catch (error) {
    console.error(`${LOG_PREFIX} El mapa de módulos no es JSON válido`, error);
    return [];
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    console.error(`${LOG_PREFIX} El mapa de módulos debe ser un objeto JSON`);
    return [];
  }

  for (const [url, selector] of Object.entries(parsed)) {
    if (typeof url !== 'string' || !url || typeof selector !== 'string' || !selector) {
      console.error(`${LOG_PREFIX} Entrada de mapa inválida`, { url, selector });
      continue;
    }

    pending.set(url, selector);
  }

  const results = await hydrateInitialDocument();
  observeFutureElements();
  return results;
}

// `ready` es el punto de sincronización explícito y verificable. No se usa
// top-level await porque los imports dinámicos no deben prometer que retienen
// DOMContentLoaded; la carga condicional continúa en paralelo.
const ready = initialize().catch((error) => {
  console.error(`${LOG_PREFIX} Falló la inicialización`, error);
  return [];
});

window.BMModules = Object.freeze({ ready });

void ready.then((results) => {
  document.dispatchEvent(
    new CustomEvent('bm:modules-ready', {
      detail: { results },
    })
  );
});
